import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { ConfirmModal } from '../ConfirmModal';
import {
  ExternalLink, Trash2, FileText, Image, File, FileImage,
  HardDriveUpload, Upload, Settings2,
} from 'lucide-react';
import { API } from '@/lib/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GOOGLE_API_KEY   = process.env.REACT_APP_GOOGLE_API_KEY;

const FileTypeIcon = ({ tipo }) => {
  if (tipo === 'imagen') return <Image className="h-8 w-8 text-sky-400" />;
  if (tipo === 'pdf')    return <FileText className="h-8 w-8 text-rose-400" />;
  return <File className="h-8 w-8 text-slate-400" />;
};

/**
 * Pestaña de radiografías y archivos del expediente.
 * Encapsula el flujo de Google Drive (picker + subida directa) y la galería.
 */
export const PatientFilesTab = ({ patientId, token, userEmail, archivos, setArchivos, formatDate }) => {
  const driveEnabled = !!(GOOGLE_CLIENT_ID && GOOGLE_API_KEY);

  const [uploadingDrive, setUploadingDrive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteArchivoId, setDeleteArchivoId] = useState(null);

  const gapiLoaded = useRef(false);
  const gisLoaded = useRef(false);
  const tokenClientRef = useRef(null);
  const pickerCallbackRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Google Drive Picker ─────────────────────────────────────────────────────

  const loadGapiScript = useCallback(() => {
    return new Promise((resolve) => {
      if (gapiLoaded.current) { resolve(); return; }
      const s = document.createElement('script');
      s.src = 'https://apis.google.com/js/api.js';
      s.onload = () => {
        window.gapi.load('picker', () => { gapiLoaded.current = true; resolve(); });
      };
      document.body.appendChild(s);
    });
  }, []);

  const loadGisScript = useCallback(() => {
    return new Promise((resolve) => {
      if (gisLoaded.current) { resolve(); return; }
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.onload = () => { gisLoaded.current = true; resolve(); };
      document.body.appendChild(s);
    });
  }, []);

  const showPicker = useCallback((accessToken) => {
    const view = new window.google.picker.DocsView()
      .setIncludeFolders(false)
      .setMimeTypes('image/jpeg,image/png,image/webp,application/pdf,image/gif');
    const picker = new window.google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(accessToken)
      .setDeveloperKey(GOOGLE_API_KEY)
      .setCallback(pickerCallbackRef.current)
      .build();
    picker.setVisible(true);
  }, []);

  const handleDrivePicker = useCallback(async () => {
    if (!driveEnabled) return;
    setUploadingDrive(true);
    try {
      await Promise.all([loadGapiScript(), loadGisScript()]);

      pickerCallbackRef.current = async (data) => {
        if (data.action !== window.google.picker.Action.PICKED) {
          setUploadingDrive(false);
          return;
        }
        const doc = data.docs[0];
        const fileId = doc.id;
        const mimeType = doc.mimeType || '';
        const tipo = mimeType.startsWith('image/') ? 'imagen'
          : mimeType === 'application/pdf' ? 'pdf'
          : 'documento';
        const url = `https://drive.google.com/file/d/${fileId}/view`;
        try {
          const res = await axios.post(`${API}/patients/${patientId}/archivos`, {
            paciente_id: patientId,
            nombre: doc.name,
            tipo,
            url,
            descripcion: null,
          });
          setArchivos(prev => [res.data, ...prev]);
          toast.success(`"${doc.name}" vinculado correctamente`);
        } catch {
          toast.error('Error al guardar el archivo');
        } finally {
          setUploadingDrive(false);
        }
      };

      if (!tokenClientRef.current) {
        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'https://www.googleapis.com/auth/drive.readonly',
          hint: userEmail,
          callback: (resp) => {
            if (resp.error) { setUploadingDrive(false); return; }
            showPicker(resp.access_token);
          },
        });
      }
      tokenClientRef.current.requestAccessToken({ prompt: '' });
    } catch {
      toast.error('Error al cargar Google Drive');
      setUploadingDrive(false);
    }
  }, [driveEnabled, loadGapiScript, loadGisScript, showPicker, patientId, setArchivos, userEmail]);

  const handleUploadToDrive = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file || !driveEnabled) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(
        `${API}/patients/${patientId}/archivos/upload`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setArchivos(prev => [res.data, ...prev]);
      toast.success(`"${file.name}" subido correctamente`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir el archivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [driveEnabled, patientId, setArchivos]);

  const handleDeleteArchivo = async () => {
    if (!deleteArchivoId) return;
    try {
      await axios.delete(`${API}/patients/${patientId}/archivos/${deleteArchivoId}`);
      setArchivos(prev => prev.filter(a => a.id !== deleteArchivoId));
      toast.success('Archivo eliminado');
    } catch {
      toast.error('Error al eliminar archivo');
    } finally {
      setDeleteArchivoId(null);
    }
  };

  // ── Helpers de URL/miniatura ────────────────────────────────────────────────

  const getDriveFileId = (url) => {
    const m = (url || '').match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    return m ? m[1] : null;
  };

  // URL del contenido privado servido por el backend (proxy autenticado).
  // El token va por query porque <img>/<a> no pueden enviar headers.
  const contentUrl = (archivo) =>
    `${API}/patients/${patientId}/archivos/${archivo.id}/contenido?token=${token}`;

  // Para abrir/descargar: archivo subido → proxy; archivo vinculado de Drive → su enlace.
  const viewUrl = (archivo) => (archivo.drive_file_id ? contentUrl(archivo) : archivo.url);

  const getThumbnail = (archivo) => {
    if (archivo.drive_file_id) {
      // Archivo subido y privado: solo previsualizamos imágenes.
      return archivo.tipo === 'imagen' ? contentUrl(archivo) : null;
    }
    const fileId = getDriveFileId(archivo.url);
    if (fileId) return `https://drive.google.com/thumbnail?id=${fileId}&sz=w400`;
    return null;
  };

  return (
    <>
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">Radiografías y Archivos</CardTitle>
            {driveEnabled ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingDrive || uploading}>
                  <Upload className="h-4 w-4 mr-1" />
                  {uploading ? 'Subiendo...' : 'Subir archivo'}
                </Button>
                <Button size="sm" onClick={handleDrivePicker} disabled={uploadingDrive || uploading}>
                  <HardDriveUpload className="h-4 w-4 mr-1" />
                  {uploadingDrive ? 'Cargando...' : 'Vincular desde Drive'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                  className="hidden"
                  onChange={handleUploadToDrive}
                />
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Settings2 className="h-4 w-4" />
                <span>Configura <code className="text-xs bg-muted px-1 rounded">REACT_APP_GOOGLE_CLIENT_ID</code> para activar Drive</span>
              </div>
            )}
          </div>
          {driveEnabled && (
            <p className="text-sm text-muted-foreground">
              Sube archivos directamente desde tu dispositivo o vincula archivos existentes de Google Drive.
            </p>
          )}
        </CardHeader>
        <CardContent>
          {archivos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {archivos.map(archivo => {
                const thumbnail = getThumbnail(archivo);
                return (
                  <div key={archivo.id} className="group relative">
                    <Dialog>
                      <DialogTrigger asChild>
                        <div
                          className="relative aspect-square rounded-xl overflow-hidden border border-border cursor-pointer hover:border-primary/50 transition-colors bg-muted/30"
                          data-testid={`file-${archivo.id}`}
                        >
                          {thumbnail ? (
                            <img
                              src={thumbnail}
                              alt={archivo.nombre}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="flex items-center justify-center w-full h-full">
                              <FileTypeIcon tipo={archivo.tipo} />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="absolute bottom-0 left-0 right-0 p-3">
                              <p className="text-white text-xs font-medium truncate">{archivo.nombre}</p>
                              <p className="text-white/70 text-xs">{formatDate(archivo.fecha)}</p>
                            </div>
                          </div>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <FileTypeIcon tipo={archivo.tipo} />
                            {archivo.nombre}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="mt-2 space-y-4">
                          {thumbnail && (
                            <img
                              src={thumbnail.replace('w400', 'w800')}
                              alt={archivo.nombre}
                              className="w-full rounded-lg border border-border"
                            />
                          )}
                          {archivo.descripcion && (
                            <p className="text-muted-foreground text-sm">{archivo.descripcion}</p>
                          )}
                          <div className="flex items-center justify-between pt-2">
                            <p className="text-xs text-muted-foreground">
                              Vinculado el {formatDate(archivo.fecha)}
                            </p>
                            <Button variant="outline" size="sm" asChild>
                              <a href={viewUrl(archivo)} target="_blank" rel="noreferrer">
                                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                {archivo.drive_file_id ? 'Abrir archivo' : 'Abrir en Drive'}
                              </a>
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Delete button */}
                    <button
                      onClick={() => setDeleteArchivoId(archivo.id)}
                      className="absolute top-2 right-2 p-1 rounded-lg bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileImage className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay archivos vinculados</p>
              {driveEnabled ? (
                <p className="text-sm mt-1">Usa "Subir archivo" para subir desde tu dispositivo o "Vincular desde Drive" para archivos existentes</p>
              ) : (
                <p className="text-sm mt-1">Configura las variables de Google Drive en <code className="text-xs">.env</code> para activar esta función</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmModal
        open={!!deleteArchivoId}
        onOpenChange={open => !open && setDeleteArchivoId(null)}
        title="¿Eliminar archivo?"
        description="El archivo se desvinculará del expediente. No se eliminará de Google Drive."
        variant="danger"
        confirmLabel="Eliminar"
        onConfirm={handleDeleteArchivo}
      />
    </>
  );
};
